import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { NotificationManager } from 'react-notifications';
import { useDispatch } from 'react-redux';
import { showModal } from 'src/redux/actions/modalActions';
import axios from 'src/api/axios';

const ResetPasswordForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    password_confirmation: '',
    token: '',
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    // Get token and email from URL parameters
    const token = searchParams.get('token');
    const email = searchParams.get('email');
    
    if (token && email) {
      setFormData(prev => ({
        ...prev,
        token: token,
        email: decodeURIComponent(email)
      }));
    } else {
      // If no token or email, redirect to login
      NotificationManager.error('Invalid or missing reset link. Please request a new password reset.');
      navigate('/login');
    }
  }, [searchParams, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setIsSubmitting(true);
    
    try {
      const response = await axios.post('api/reset-password', formData, {
        disableNotification: true // Disable axios interceptor notifications, we handle errors manually
      });
      
      NotificationManager.success(response.data.message || 'Password has been reset successfully!');
      // Redirect to login after successful reset
      setTimeout(() => {
        navigate('/login');
      }, 1500);
    } catch (error) {
      console.error('Reset password error:', error);
      console.error('Error response:', error.response?.data);
      
      if (!error.response) {
        NotificationManager.error('No server response');
      } else if (error.response?.status === 400 || error.response?.status === 401 || error.response?.status === 422) {
        // Handle validation errors from backend
        const errorData = error.response.data;
        
        // Laravel validation errors format: { error: { field: ["message1", "message2"] } }
        const validationErrors = errorData.error || errorData.errors || {};
        
        if (validationErrors && typeof validationErrors === 'object' && !Array.isArray(validationErrors)) {
          // Format errors for display: { field: "first message" }
          const formattedErrors = {};
          
          for (const [key, value] of Object.entries(validationErrors)) {
            if (Array.isArray(value) && value.length > 0) {
              // Take the first error message for each field
              formattedErrors[key] = typeof value[0] === 'string' ? value[0] : String(value[0]);
            } else if (typeof value === 'string') {
              formattedErrors[key] = value;
            }
          }
          
          setErrors(formattedErrors);
          
          // Also show a general notification
          NotificationManager.error('Please correct the errors in the form');
        } else if (errorData.error) {
          NotificationManager.error(String(errorData.error));
        } else if (errorData.message) {
          NotificationManager.error(String(errorData.message));
        } else {
          NotificationManager.error('Failed to reset password. Please try again.');
        }
      } else {
        NotificationManager.error(error.message || 'An error occurred');
        if (error.response?.data?.message) {
          NotificationManager.error(error.response.data.message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevFormData => ({ ...prevFormData, [name]: value }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <>
      <div className="w-full mx-auto bg-white md:mt-0 sm:max-w-md lg:max-w-[496px] py-8">
        <div>
          <h2 className="text-[35px] leading-[45px] font-bold text-center">
            Reset Password
          </h2>
          <p className="text-base mt-4 mb-6 text-center text-gray-600">
            Enter your new password below
          </p>
          <form className="" onSubmit={handleSubmit}>
            <div>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full mt-[30px] p-2.5 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Email"
                required
                disabled={true}
              />
              {errors.email && (
                <p className="text-red-500 text-xs mt-1">{errors.email}</p>
              )}
            </div>
            
            <div>
              <input
                type="password"
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="New Password"
                className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full mt-5 p-2.5 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                required
                disabled={isSubmitting}
              />
              {errors.password && (
                <p className="text-red-500 text-xs mt-1">{errors.password}</p>
              )}
            </div>
            
            <div>
              <input
                type="password"
                id="password_confirmation"
                name="password_confirmation"
                value={formData.password_confirmation}
                onChange={handleChange}
                placeholder="Confirm New Password"
                className={`bg-gray-50 border sm:text-sm rounded focus:ring-[#832EE6] focus:border-[#832EE6] block w-full mt-5 p-2.5 ${errors.password_confirmation ? 'border-red-500' : 'border-gray-300'}`}
                required
                disabled={isSubmitting}
              />
              {errors.password_confirmation && (
                <p className="text-red-500 text-xs mt-1">{errors.password_confirmation}</p>
              )}
            </div>
            
            <div className="flex mt-10 mb-12">
              <button 
                type="submit"
                disabled={isSubmitting}
                className={`bg-[#832EE6] hover:bg-purple-800 text-white px-[45px] mx-auto py-[15px] text-[18px] leading-6 font-bold inline-block rounded-[27px] ${isSubmitting ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </form>

          <p className="text-base text-center">
            Remember your password? <span onClick={() => dispatch(showModal('LOGIN_MODAL'))} className="font-bold text-[#832EE6] cursor-pointer">Log in</span>
          </p>
        </div>
      </div>
    </>
  );
};

export default ResetPasswordForm;

